import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layers, Star, Users, ArrowUpRight, Trash2, Loader2, Zap, ArrowRight } from 'lucide-react';
import { getMyStackTools, removeFromStack, getToolUrl } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import './MyStack.css';

export default function MyStack() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const { user, displayName } = useAuth();
  const navigate = useNavigate();

  // Redirect if not logged in
  useEffect(() => {
    if (!user && !loading) navigate('/signup');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getMyStackTools()
      .then(setTools)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const handleRemove = async (toolId) => {
    setRemoving(toolId);
    try {
      await removeFromStack(toolId);
      setTools(prev => prev.filter(t => t.id !== toolId));
    } catch (err) {
      console.error(err);
    } finally {
      setRemoving(null);
    }
  };

  // Category breakdown
  const categories = [...new Set(tools.map(t => t.category))];

  return (
    <main className="stack-page" style={{ paddingTop: 'var(--nav-h)' }}>
      <section className="section">
        <div className="section-inner">
          {/* Header */}
          <div className="stack-page__header">
            <div>
              <div className="section-label"><Layers size={12} /> My Stack</div>
              <h1 className="section-title">
                {displayName}'s <span className="grad-text">Tool Stack</span>
              </h1>
              {!loading && tools.length > 0 && (
                <p className="section-sub">
                  {tools.length} tool{tools.length !== 1 ? 's' : ''} saved
                  {categories.length > 1 && ` across ${categories.length} categories`}.
                </p>
              )}
            </div>
            <Link to="/tools" className="btn btn-ghost">
              Browse catalog <ArrowRight size={15} />
            </Link>
          </div>

          {/* Loading */}
          {loading ? (
            <div className="stack-page__empty glass">
              <Loader2 size={28} className="tools-page__spinner" style={{ color: 'var(--text-accent)' }} />
              <p style={{ color: 'var(--text-muted)' }}>Loading your stack…</p>
            </div>

          /* Empty state */
          ) : tools.length === 0 ? (
            <div className="stack-page__empty glass">
              <div className="stack-page__empty-icon">
                <Layers size={32} />
              </div>
              <h3 className="stack-page__empty-title">Your stack is empty</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', maxWidth: 320 }}>
                Browse the tool catalog and hit the bookmark icon on any tool to save it here.
              </p>
              <Link to="/tools" className="btn btn-primary">
                <Zap size={15} /> Discover tools
              </Link>
            </div>

          ) : (
            <>
              {/* Category pills */}
              {categories.length > 1 && (
                <div className="stack-page__cats">
                  {categories.map(cat => {
                    const count = tools.filter(t => t.category === cat).length;
                    return (
                      <span key={cat} className="stack-page__cat tag">
                        {cat} <span className="stack-page__cat-count">{count}</span>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Tool grid */}
              <div className="stack-page__grid">
                {tools.map(tool => (
                  <div key={tool.id} className="stack-card glass">
                    {/* Remove button */}
                    <button
                      className="stack-card__remove"
                      onClick={() => handleRemove(tool.id)}
                      disabled={removing === tool.id}
                      aria-label="Remove from stack"
                      title="Remove from stack"
                    >
                      {removing === tool.id
                        ? <Loader2 size={13} className="save-btn__spin" />
                        : <Trash2 size={13} />
                      }
                    </button>

                    <div className="stack-card__top">
                      <div
                        className="stack-card__icon"
                        style={{ background: `${tool.logo_color}18`, border: `1px solid ${tool.logo_color}30` }}
                      >
                        <span style={{ color: tool.logo_color, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20 }}>
                          {tool.name[0]}
                        </span>
                      </div>
                      <div>
                        <div className="stack-card__name">{tool.name}</div>
                        <div className="tag">{tool.category}</div>
                      </div>
                    </div>

                    <p className="stack-card__tagline">{tool.tagline}</p>

                    <div className="stack-card__meta">
                      <div className="tool-card__score">
                        <Star size={12} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                        <span>{tool.score}</span>
                      </div>
                      <span className="tool-page-card__price">{tool.price_display}</span>
                    </div>

                    <a
                      href={getToolUrl(tool)}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-ghost btn-sm"
                      style={{ marginTop: 'auto' }}
                    >
                      Visit <ArrowUpRight size={13} />
                    </a>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
